import { useMemo, useState } from 'react';
import { CalendarCheck, CalendarPlus, CalendarX, ChevronDown, Clock, Download } from 'lucide-react';

import { Select } from '@/components/ui/Select';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { formatDateTime, formatMonth } from '@/lib/format';
import type { ParentAttendanceMonthData } from '../../types/attendance';
import type { ParentChild } from '../../types/parent';
import { ATTENDANCE_MARK_LABEL } from './AttendanceStatusPill';
import { downloadAttendancePdf } from './attendancePdf';

export interface AttendanceReportViewProps {
  child: ParentChild | null;
  /**
   * The month the page is showing — the same object the Attendance tab draws,
   * so the two tabs can never disagree about one child's register.
   */
  monthData: ParentAttendanceMonthData;
  onMonthChange: (monthKey: string) => void;
}

export function AttendanceReportView({
  child,
  monthData,
  onMonthChange,
}: AttendanceReportViewProps) {
  const branding = useInstituteBranding((state) => state.branding);
  const [trendRange, setTrendRange] = useState<'6m' | '3m' | 'year'>('6m');
  const [hoveredTrendIndex, setHoveredTrendIndex] = useState<number | null>(null);
  const [isTrendRangeOpen, setIsTrendRangeOpen] = useState(false);

  // The month the page owns. Changing it here changes it for both tabs, which
  // is the point: a report tab that fetched its own month would let a parent
  // read May's calendar beside April's totals.
  const selectedMonthKey = monthData.monthKey;
  const months = monthData.months;
  const currentMonthOption =
    months.find((option) => option.key === selectedMonthKey) ?? months[0];

  const stats = monthData.stats;
  const leaveDaysCount = stats.leaveDays ?? 0;

  // Percentage breakdown for summary, over the days actually marked. Zero here
  // only sizes the donut; every row prints "—" until a register exists, since a
  // school that has marked nothing must not be shown 0%, which a family reads
  // as "your child attended nothing".
  const share = (days: number) =>
    stats.markedDays > 0 ? Math.round((days / stats.markedDays) * 100) : 0;

  // Present on its own, not `overallPercent`: the server counts a late day as
  // attended, so the overall figure includes the late days the Late row
  // already shows and the breakdown would add up to more than the register.
  const presentPct = share(stats.presentDays);
  const absentPct = share(stats.absentDays);
  const latePct = share(stats.lateDays);
  const leavePct = share(leaveDaysCount);

  // Donut chart calculations for Summary Card
  const donutRadius = 56;
  const donutCircumference = 2 * Math.PI * donutRadius;
  const presentDash = (presentPct / 100) * donutCircumference;
  const absentDash = (absentPct / 100) * donutCircumference;
  const lateDash = (latePct / 100) * donutCircumference;
  const leaveDash = (leavePct / 100) * donutCircumference;

  const presentOffset = 0;
  const absentOffset = -presentDash;
  const lateOffset = -(presentDash + absentDash);
  const leaveOffset = -(presentDash + absentDash + lateDash);

  // The range decides which months the axis holds, not which months have marks:
  // slicing only the marked months drew the same chart for every range whenever
  // a child had fewer marked months than the shortest one. A month with no
  // register stays on the axis with no dots, so nothing is invented for it, and
  // a school three months into its year still has only those three to offer.
  const trendSlots = useMemo(() => {
    const marksByMonth = new Map(monthData.trend.map((point) => [point.monthKey, point]));
    const oldestFirst = [...months].reverse();
    const inRange =
      trendRange === '3m'
        ? oldestFirst.slice(-3)
        : trendRange === '6m'
          ? oldestFirst.slice(-6)
          : oldestFirst;

    return inRange.map((option) => ({
      monthKey: option.key,
      month: option.label,
      // "Sep 2026": the full name is for the tooltip, as six of them side by side
      // run into each other on the axis.
      axisLabel: formatMonth(`${option.key}-01`),
      marks: marksByMonth.get(option.key) ?? null,
    }));
  }, [months, monthData.trend, trendRange]);

  // The month's days in date order, for the downloaded report.
  const chronologicalRecords = useMemo(() => {
    return [...monthData.records].sort((a, b) => a.date.localeCompare(b.date));
  }, [monthData.records]);

  // Export report PDF
  const handleExportReport = () => {
    const fileName = `Attendance_Report_${child?.fullName ?? 'Student'}_${currentMonthOption.label.replace(/\s+/g, '_')}.pdf`;

    downloadAttendancePdf({
      fileName,
      instituteName: branding?.displayName ?? 'Smart School Management System',
      studentName: child?.fullName ?? 'Student',
      className: child?.className ?? '—',
      rollNumber: child?.rollNumber,
      academicYear: monthData.academicYear.name,
      month: currentMonthOption.label,
      generatedAt: formatDateTime(new Date()),
      overallAttendance: stats.overallPercent !== null ? `${stats.overallPercent}%` : '—',
      presentDays: stats.presentDays,
      absentDays: stats.absentDays,
      lateDays: stats.lateDays,
      leaveDays: leaveDaysCount,
      records: chronologicalRecords.map((r) => ({
        date: r.formattedDate,
        day: r.day,
        status: ATTENDANCE_MARK_LABEL[r.status],
        remarks: r.remarks ?? undefined,
      })),
    });
  };

  // SVG Chart Dimensions for Trend
  const chartWidth = 620;
  const chartHeight = 220;
  const padLeft = 46;
  const padRight = 24;
  const padTop = 20;
  const padBottom = 34;

  const innerWidth = chartWidth - padLeft - padRight;
  const innerHeight = chartHeight - padTop - padBottom;

  const getX = (index: number) => {
    if (trendSlots.length <= 1) return padLeft + innerWidth / 2;
    return padLeft + (index / (trendSlots.length - 1)) * innerWidth;
  };

  const getY = (percent: number) => {
    const clamped = Math.max(0, Math.min(100, percent));
    return padTop + innerHeight - (clamped / 100) * innerHeight;
  };

  // Generate smooth SVG paths
  const makeSmoothPath = (points: { x: number; y: number }[]) => {
    if (points.length === 0) return '';
    if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      const cx = (p0.x + p1.x) / 2;
      path += ` C ${cx} ${p0.y}, ${cx} ${p1.y}, ${p1.x} ${p1.y}`;
    }
    return path;
  };

  const markedPoints = trendSlots.flatMap((slot, index) =>
    slot.marks ? [{ x: getX(index), marks: slot.marks }] : [],
  );
  const presentCoordPoints = markedPoints.map(({ x, marks }) => ({ x, y: getY(marks.present) }));
  const absentCoordPoints = markedPoints.map(({ x, marks }) => ({ x, y: getY(marks.absent) }));
  const lateCoordPoints = markedPoints.map(({ x, marks }) => ({ x, y: getY(marks.late) }));

  // More than eight "Sep 2026" labels overlap at this width, so a longer axis
  // labels every other month, counted back from the latest so the newest always
  // has its name.
  const labelEvery = trendSlots.length > 8 ? 2 : 1;

  const trendNotice =
    markedPoints.length >= 2
      ? null
      : monthData.trend.length === 0
        ? 'No attendance has been marked this session yet, so there is no trend to show.'
        : monthData.trend.length === 1
          ? 'The trend line appears once a second month of attendance is marked.'
          : markedPoints.length === 0
            ? 'No attendance was marked in this period. Choose a longer period to see the trend.'
            : 'Only one month in this period has attendance marked. Choose a longer period to see the trend.';

  const presentLinePath = makeSmoothPath(presentCoordPoints);
  const absentLinePath = makeSmoothPath(absentCoordPoints);
  const lateLinePath = makeSmoothPath(lateCoordPoints);

  const presentAreaPath =
    presentCoordPoints.length > 0
      ? `${presentLinePath} L ${presentCoordPoints[presentCoordPoints.length - 1].x} ${padTop + innerHeight} L ${presentCoordPoints[0].x} ${padTop + innerHeight} Z`
      : '';

  return (
    <div className="flex flex-col gap-5">
      {/* ---------------- 1. Filter Header Row ---------------- */}
      <div className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-xs">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/*
            Two facts and one choice.

            The session and the child's class are read from the server and
            printed; only the month is chosen, because only the month is a
            question this endpoint can answer. There were two more dropdowns
            here — an "Academic Year" listing 2026-27 and 2025-26, and a "Term"
            listing Apr–Sep and Oct–Mar — and neither refetched anything. The
            endpoint answers for the current session alone, and this product has
            no term: `AcademicYear` has a start and an end, and nothing divides
            it. Picking either only relabelled the CSV header.
          */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 flex-1">
            <div className="flex flex-col justify-end">
              <span className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1">
                Academic year
              </span>
              <span className="flex h-10 items-center rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-800/90 px-3 text-sm font-bold text-slate-800 dark:text-slate-200">
                {monthData.academicYear.name}
              </span>
            </div>

            <div className="flex flex-col justify-end">
              <span className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 mb-1">
                Class
              </span>
              <span className="flex h-10 items-center rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/90 dark:bg-slate-800/90 px-3 text-sm font-bold text-slate-800 dark:text-slate-200 truncate">
                {child?.className ?? '—'}
              </span>
            </div>

            {/* The shared `Select`, not a fourth hand-rolled dropdown with its
                own click-away overlay — this one is keyboard-operable and
                searchable like every other picker in the product (§3). */}
            <Select
              label="Month"
              options={months.map((option) => ({ value: option.key, label: option.label }))}
              value={selectedMonthKey}
              hideSearch
              onChange={(event) => onMonthChange(event.target.value)}
              className="h-10 text-sm font-semibold"
            />
          </div>

          {/* Download Report Button */}
          <div className="lg:self-end pt-1 sm:pt-0">
            <button
              type="button"
              onClick={handleExportReport}
              className="w-full sm:w-auto  rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white px-4  py-2 text-sm tracking-wide sm:text-sm font-bold inline-flex items-center justify-center gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98]"
            >
              <Download className="h-4 w-4" />
              <span>Download Report</span>
            </button>
          </div>
        </div>
      </div>

      {/* ---------------- 2. Five Metric KPI Cards ---------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {/* Card 1: Overall Attendance */}
        <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-emerald-200/90 dark:border-slate-800 bg-emerald-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
          <div>
            <p className="text-sm sm:text-[14px] font-bold text-emerald-800 dark:text-emerald-400 leading-tight">
              Overall Attendance
            </p>
            <p className="mt-0.5 text-2xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight tabular-nums truncate">
              {stats.overallPercent === null ? '—' : `${stats.overallPercent}%`}
            </p>
          </div>

          <div className="mt-2.5">
            <div className="h-1.5 w-full rounded-full bg-emerald-200/80 dark:bg-slate-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-[#059669] dark:bg-emerald-500 transition-all duration-700 ease-out"
                style={{ width: `${Math.min(100, Math.max(0, stats.overallPercent ?? 0))}%` }}
              />
            </div>
            <div className="mt-1.5 flex items-center justify-between text-[10.5px] sm:text-[11px] font-semibold text-emerald-700/90 dark:text-slate-400">
              <span>Present: {stats.presentDays} {stats.presentDays === 1 ? 'Day' : 'Days'}</span>
              <span>Working days: {stats.workingDays}</span>
            </div>
          </div>
        </div>

        {/* Card 2: Present Days */}
        <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-sky-200/90 dark:border-slate-800 bg-sky-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-bold text-sky-800 dark:text-sky-400 leading-tight">
                Present Days
              </p>
              <p className="mt-0.5 text-2xl font-black text-sky-600 dark:text-sky-400 tracking-tight tabular-nums truncate">
                {stats.presentDays}
              </p>
              <p className="mt-0.5 sm:mt-1 text-[11px] font-semibold text-sky-600/90 dark:text-slate-400">
                {monthData.label}
              </p>
            </div>
            <span
              className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
              style={{ backgroundColor: '#0284c7' }}
              aria-hidden
            >
              <CalendarCheck className="h-5 w-5" />
            </span>
          </div>
        </div>

        {/* Card 3: Absent Days */}
        <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-rose-200/90 dark:border-slate-800 bg-rose-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-bold text-rose-800 dark:text-rose-400 leading-tight">
                Absent Days
              </p>
              <p className="mt-0.5 text-2xl font-black text-rose-600 dark:text-rose-400 tracking-tight tabular-nums truncate">
                {stats.absentDays}
              </p>
              <p className="mt-0.5 sm:mt-1 text-[11px] font-semibold text-rose-600/90 dark:text-slate-400">
                {monthData.label}
              </p>
            </div>
            <span
              className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
              style={{ backgroundColor: '#e11d48' }}
              aria-hidden
            >
              <CalendarX className="h-5 w-5" />
            </span>
          </div>
        </div>

        {/* Card 4: Late Days */}
        <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-amber-200/90 dark:border-slate-800 bg-amber-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-bold text-amber-800 dark:text-amber-400 leading-tight">
                Late Days
              </p>
              <p className="mt-0.5 text-2xl font-black text-amber-600 dark:text-amber-400 tracking-tight tabular-nums truncate">
                {stats.lateDays}
              </p>
              <p className="mt-0.5 sm:mt-1 text-[11px] font-semibold text-amber-600/90 dark:text-slate-400">
                {monthData.label}
              </p>
            </div>
            <span
              className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
              style={{ backgroundColor: '#d97706' }}
              aria-hidden
            >
              <Clock className="h-5 w-5" />
            </span>
          </div>
        </div>

        {/* Card 5: Leave Days */}
        <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-purple-200/90 dark:border-slate-800 bg-purple-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200 sm:col-span-2 md:col-span-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-bold text-purple-800 dark:text-purple-400 leading-tight">
                Leave Days
              </p>
              <p className="mt-0.5 text-2xl font-black text-purple-600 dark:text-purple-400 tracking-tight tabular-nums truncate">
                {leaveDaysCount}
              </p>
              <p className="mt-0.5 sm:mt-1 text-[11px] font-semibold text-purple-600/90 dark:text-slate-400">
                {monthData.label}
              </p>
            </div>
            <span
              className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
              style={{ backgroundColor: '#7c3aed' }}
              aria-hidden
            >
              <CalendarPlus className="h-5 w-5" />
            </span>
          </div>
        </div>
      </div>

      {/* ---------------- 3. Middle Section: Attendance Trend & Summary ---------------- */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left: Attendance Trend Line/Area Chart */}
        <div className="lg:col-span-7 rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-xs flex flex-col justify-between">
          <div>
            {/* Header + Filter + Legend */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                Attendance Trend
              </h3>

              <div className="flex flex-wrap items-center gap-3">
                {/* Legend */}
                <div className="flex items-center gap-3 text-xs font-semibold text-slate-600 dark:text-slate-400">
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" />
                    <span>Present</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-rose-500" />
                    <span>Absent</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-amber-500" />
                    <span>Late</span>
                  </div>
                </div>

                {/* Range Selector */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsTrendRangeOpen((prev) => !prev)}
                    className="flex h-8 items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-2.5 text-xs font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                  >
                    <span>
                      {trendRange === '6m'
                        ? 'Last 6 Months'
                        : trendRange === '3m'
                          ? 'Last 3 Months'
                          : 'Academic Year'}
                    </span>
                    <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                  </button>

                  {isTrendRangeOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-20"
                        onClick={() => setIsTrendRangeOpen(false)}
                      />
                      <div className="absolute right-0 mt-1 w-36 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 shadow-xl z-30">
                        <button
                          type="button"
                          onClick={() => {
                            setTrendRange('6m');
                            setIsTrendRangeOpen(false);
                          }}
                          className="flex w-full items-center justify-between px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                        >
                          <span>Last 6 Months</span>
                          {trendRange === '6m' && <span>✓</span>}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setTrendRange('3m');
                            setIsTrendRangeOpen(false);
                          }}
                          className="flex w-full items-center justify-between px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                        >
                          <span>Last 3 Months</span>
                          {trendRange === '3m' && <span>✓</span>}
                        </button>
                        {/* The whole session's trend is already in the
                            response, so the year is one more slice of it. */}
                        <button
                          type="button"
                          onClick={() => {
                            setTrendRange('year');
                            setIsTrendRangeOpen(false);
                          }}
                          className="flex w-full items-center justify-between px-3 py-1.5 text-xs font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                        >
                          <span>Academic Year</span>
                          {trendRange === 'year' && <span>✓</span>}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* SVG Trend Chart */}
            <div className="relative w-full mt-3">
              <svg
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                className="w-full h-auto select-none"
              >
                <defs>
                  <linearGradient id="presentGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity="0.28" />
                    <stop offset="100%" stopColor="#10b981" stopOpacity="0.0" />
                  </linearGradient>
                </defs>

                {/* Y-Axis Grid Lines & Labels */}
                {[100, 75, 50, 25, 0].map((val) => {
                  const y = getY(val);
                  return (
                    <g key={val}>
                      <line
                        x1={padLeft}
                        y1={y}
                        x2={chartWidth - padRight}
                        y2={y}
                        stroke="currentColor"
                        className="text-slate-100 dark:text-slate-800/80"
                        strokeDasharray="4 4"
                        strokeWidth="1"
                      />
                      <text
                        x={padLeft - 8}
                        y={y + 3.5}
                        textAnchor="end"
                        className="text-[10px] font-semibold fill-slate-400 dark:fill-slate-500"
                      >
                        {val}%
                      </text>
                    </g>
                  );
                })}

                {/* Shaded Area for Present */}
                <path d={presentAreaPath} fill="url(#presentGradient)" />

                {/* Present Line */}
                <path
                  d={presentLinePath}
                  fill="none"
                  stroke="#10b981"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* Absent Line */}
                <path
                  d={absentLinePath}
                  fill="none"
                  stroke="#f43f5e"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* Late Line */}
                <path
                  d={lateLinePath}
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* X-Axis Data Points & Vertical Hover Highlights */}
                {trendSlots.map((slot, index) => {
                  const x = getX(index);
                  const isHovered = hoveredTrendIndex === index;
                  const columnWidth = innerWidth / Math.max(trendSlots.length - 1, 1);

                  return (
                    <g
                      key={slot.monthKey}
                      onMouseEnter={() => setHoveredTrendIndex(index)}
                      onMouseLeave={() => setHoveredTrendIndex(null)}
                      className="cursor-pointer"
                    >
                      {/* The whole column answers to hover: a month with no
                          register has no dots to point at. */}
                      <rect
                        x={x - columnWidth / 2}
                        y={padTop}
                        width={columnWidth}
                        height={innerHeight}
                        fill="transparent"
                      />

                      {/* Vertical line indicator on hover */}
                      {isHovered && (
                        <line
                          x1={x}
                          y1={padTop}
                          x2={x}
                          y2={padTop + innerHeight}
                          stroke="#6366f1"
                          strokeWidth="1.5"
                          strokeDasharray="3 3"
                          opacity="0.6"
                        />
                      )}

                      {slot.marks && (
                        <>
                          {/* Present Dot */}
                          <circle
                            cx={x}
                            cy={getY(slot.marks.present)}
                            r={isHovered ? 5 : 3.5}
                            fill="#10b981"
                            stroke="#ffffff"
                            strokeWidth="2"
                            className="transition-[r]"
                          />

                          {/* Absent Dot */}
                          <circle
                            cx={x}
                            cy={getY(slot.marks.absent)}
                            r={isHovered ? 4.5 : 3}
                            fill="#f43f5e"
                            stroke="#ffffff"
                            strokeWidth="1.5"
                            className="transition-[r]"
                          />

                          {/* Late Dot */}
                          <circle
                            cx={x}
                            cy={getY(slot.marks.late)}
                            r={isHovered ? 4.5 : 3}
                            fill="#f59e0b"
                            stroke="#ffffff"
                            strokeWidth="1.5"
                            className="transition-[r]"
                          />
                        </>
                      )}

                      {/* X-Axis Month Label */}
                      {(trendSlots.length - 1 - index) % labelEvery === 0 && (
                        <text
                          x={x}
                          y={chartHeight - 10}
                          // The two ends read inward, or their names run off the chart.
                          textAnchor={
                            trendSlots.length > 1 && index === 0
                              ? 'start'
                              : trendSlots.length > 1 && index === trendSlots.length - 1
                                ? 'end'
                                : 'middle'
                          }
                          className={`text-[11px] font-bold ${
                            isHovered
                              ? 'fill-indigo-600 dark:fill-indigo-400 font-extrabold'
                              : 'fill-slate-500 dark:fill-slate-400'
                          }`}
                        >
                          {slot.axisLabel}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>

              {/* A line needs two months. Without them the grid is drawn empty, or
                  with one dot, and read as a chart that failed to load — so it
                  says why instead. */}
              {trendNotice && (
                <div className="absolute inset-0 grid place-items-center p-4 pointer-events-none">
                  <p className="max-w-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-center text-sm font-medium text-slate-600 dark:text-slate-300 shadow-xs">
                    {trendNotice}
                  </p>
                </div>
              )}

              {/* Hover Tooltip Overlay */}
              {hoveredTrendIndex !== null && (() => {
                const slot = trendSlots[hoveredTrendIndex];
                if (!slot) return null;
                const point = slot.marks;
                const xPercent = (getX(hoveredTrendIndex) / chartWidth) * 100;
                const yPercent = (getY(point?.present ?? 50) / chartHeight) * 100;

                // When in the upper half of chart, flip below the point so it never clips at the top
                const isTopHalf = yPercent < 48;
                // When near horizontal edges, anchor inward so it never clips on the left or right
                const isNearLeft = xPercent < 22;
                const isNearRight = xPercent > 78;

                const translateX = isNearLeft ? '0%' : isNearRight ? '-100%' : '-50%';
                const translateY = isTopHalf ? '0%' : '-100%';
                const topOffset = isTopHalf ? '14px' : '-14px';

                return (
                  <div
                    className="absolute pointer-events-none rounded-xl bg-slate-900 text-white px-3 py-2 text-xs shadow-xl z-30 whitespace-nowrap border border-slate-700/60 transition-[left,top] duration-100 ease-out"
                    style={{
                      left: `${xPercent}%`,
                      top: `calc(${yPercent}% + ${topOffset})`,
                      transform: `translate(${translateX}, ${translateY})`,
                    }}
                  >
                    <p className="font-bold border-b border-slate-700 pb-1 mb-1">
                      {slot.month}
                    </p>
                    {point ? (
                      <div className="flex flex-col gap-0.5 text-[11px]">
                        <span className="text-emerald-400">
                          Present: {point.present}%
                        </span>
                        <span className="text-rose-400">
                          Absent: {point.absent}%
                        </span>
                        <span className="text-amber-400">
                          Late: {point.late}%
                        </span>
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-300">No attendance marked</p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right: Attendance Summary (Donut Chart + Breakdown Table) */}
        <div className="lg:col-span-5 rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-xs flex flex-col justify-between">
          <div>
            <div className="pb-3 border-b border-slate-100 dark:border-slate-800">
              <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                Attendance Summary ({currentMonthOption.label})
              </h3>
            </div>

            {/* Donut + Table Container */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center mt-3">
              {/* Donut Chart */}
              <div className="sm:col-span-5 flex flex-col items-center justify-center">
                <div className="relative flex h-36 w-36 items-center justify-center">
                  <svg className="h-36 w-36 -rotate-90" viewBox="0 0 140 140">
                    {/* Background track circle */}
                    <circle
                      cx="70"
                      cy="70"
                      r={donutRadius}
                      className="stroke-slate-100 dark:stroke-slate-800"
                      strokeWidth="14"
                      fill="transparent"
                    />

                    {/* Present slice (Green) */}
                    {presentDash > 0 && (
                      <circle
                        cx="70"
                        cy="70"
                        r={donutRadius}
                        className="stroke-[#10b981] transition-all duration-700"
                        strokeWidth="14"
                        strokeDasharray={`${presentDash} ${donutCircumference - presentDash}`}
                        strokeDashoffset={presentOffset}
                        fill="transparent"
                      />
                    )}

                    {/* Absent slice (Red) */}
                    {absentDash > 0 && (
                      <circle
                        cx="70"
                        cy="70"
                        r={donutRadius}
                        className="stroke-[#f43f5e] transition-all duration-700"
                        strokeWidth="14"
                        strokeDasharray={`${absentDash} ${donutCircumference - absentDash}`}
                        strokeDashoffset={absentOffset}
                        fill="transparent"
                      />
                    )}

                    {/* Late slice (Amber) */}
                    {lateDash > 0 && (
                      <circle
                        cx="70"
                        cy="70"
                        r={donutRadius}
                        className="stroke-[#f59e0b] transition-all duration-700"
                        strokeWidth="14"
                        strokeDasharray={`${lateDash} ${donutCircumference - lateDash}`}
                        strokeDashoffset={lateOffset}
                        fill="transparent"
                      />
                    )}

                    {/* Leave slice (Purple) — the same colour as its row in
                        the table beside it, so the ring adds up to the register. */}
                    {leaveDash > 0 && (
                      <circle
                        cx="70"
                        cy="70"
                        r={donutRadius}
                        className="stroke-purple-500 transition-all duration-700"
                        strokeWidth="14"
                        strokeDasharray={`${leaveDash} ${donutCircumference - leaveDash}`}
                        strokeDashoffset={leaveOffset}
                        fill="transparent"
                      />
                    )}
                  </svg>

                  {/* Center Text */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-2xl font-black text-slate-900 dark:text-white leading-tight">
                      {stats.markedDays}
                    </span>
                    <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
                      Days Marked
                    </span>
                  </div>
                </div>
              </div>

              {/* Breakdown Table */}
              <div className="sm:col-span-7 overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse min-w-[220px]">
                  <thead>
                    <tr className="border-b border-slate-100 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-sm font-bold">
                      <th className="py-1.5 px-2">Status</th>
                      <th className="py-1.5 px-2 text-center">Days</th>
                      <th className="py-1.5 px-2 text-right">Percentage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-2 px-2 font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                        <span>Present</span>
                      </td>
                      <td className="py-2 px-2 text-center font-bold text-slate-800 dark:text-slate-200">
                        {stats.presentDays}
                      </td>
                      <td className="py-2 px-2 text-right font-black text-emerald-600 dark:text-emerald-400">
                        {stats.markedDays > 0 ? `${presentPct}%` : '—'}
                      </td>
                    </tr>

                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-2 px-2 font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-rose-500 shrink-0" />
                        <span>Absent</span>
                      </td>
                      <td className="py-2 px-2 text-center font-bold text-slate-800 dark:text-slate-200">
                        {stats.absentDays}
                      </td>
                      <td className="py-2 px-2 text-right font-black text-rose-600 dark:text-rose-400">
                        {stats.markedDays > 0 ? `${absentPct}%` : '—'}
                      </td>
                    </tr>

                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-2 px-2 font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" />
                        <span>Late</span>
                      </td>
                      <td className="py-2 px-2 text-center font-bold text-slate-800 dark:text-slate-200">
                        {stats.lateDays}
                      </td>
                      <td className="py-2 px-2 text-right font-black text-amber-600 dark:text-amber-400">
                        {stats.markedDays > 0 ? `${latePct}%` : '—'}
                      </td>
                    </tr>

                    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-2 px-2 font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-purple-500 shrink-0" />
                        <span>Leave</span>
                      </td>
                      <td className="py-2 px-2 text-center font-bold text-slate-800 dark:text-slate-200">
                        {leaveDaysCount}
                      </td>
                      <td className="py-2 px-2 text-right font-black text-purple-600 dark:text-purple-400">
                        {stats.markedDays > 0 ? `${leavePct}%` : '—'}
                      </td>
                    </tr>

                    <tr className="bg-slate-50/70 dark:bg-slate-800/50 font-bold border-t border-slate-200 dark:border-slate-700">
                      <td className="py-2 px-2 text-slate-900 dark:text-white">
                        Days Marked
                      </td>
                      <td className="py-2 px-2 text-center text-slate-900 dark:text-white">
                        {stats.markedDays}
                      </td>
                      <td className="py-2 px-2 text-right text-slate-900 dark:text-white font-black">
                        {stats.markedDays > 0 ? '100%' : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
